import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import './Terms.css';

const SECTIONS = [
  {
    id: 'introduction',
    label: 'Introduction',
  },
  {
    id: 'information',
    label: 'Information We Collect',
  },
  {
    id: 'use',
    label: 'How We Use Information',
  },
  {
    id: 'sharing',
    label: 'Information Sharing',
  },
  {
    id: 'cookies',
    label: 'Cookies & Tracking',
  },
  {
    id: 'security',
    label: 'Data Security',
  },
  {
    id: 'retention',
    label: 'Data Retention',
  },
  {
    id: 'rights',
    label: 'Your Privacy Rights',
  },
  {
    id: 'children',
    label: "Children's Privacy",
  },
  {
    id: 'changes',
    label: 'Policy Changes',
  },
  {
    id: 'contact',
    label: 'Contact Us',
  },
];

const CLAUSES = {
  information: [
    {
      n: '01',
      title: 'Information You Provide',
      text: 'We may collect information that you voluntarily provide when you contact SHIVKMS, request a quotation, purchase our services, register for an account, submit an inquiry, communicate with our team, or otherwise interact with our website or services. This may include your name, business name, email address, telephone number, billing information, business information, and other information you choose to provide.',
    },
    {
      n: '02',
      title: 'Account & Business Information',
      text: 'When you use SHIVKMS software, applications, portals, ERP systems, websites, or other business solutions, we may process information required to create and administer your account and provide the requested services. Depending on the service, this may include user profiles, organization details, branch information, transaction-related information, and system configuration data.',
    },
    {
      n: '03',
      title: 'Information Collected Automatically',
      text: 'When you visit our website or use certain online services, technical information may be collected automatically. This may include IP address, browser type, operating system, device information, pages visited, approximate location derived from technical information, referring pages, access times, and information about how you interact with our website.',
    },
    {
      n: '04',
      title: 'Communications',
      text: 'If you contact us by email, telephone, WhatsApp, contact forms, or other communication channels, we may retain the information necessary to respond to your request, provide support, maintain business records, and improve our services.',
    },
  ],

  use: [
    {
      n: '05',
      title: 'Providing Our Services',
      text: 'We use information to provide, operate, maintain, customize, and support SHIVKMS software, websites, applications, portals, and related services requested by our clients.',
    },
    {
      n: '06',
      title: 'Communication',
      text: 'We may use your contact information to respond to inquiries, provide technical support, send service-related notifications, communicate regarding billing or subscriptions, and provide important information relating to products or services you use.',
    },
    {
      n: '07',
      title: 'Business Operations',
      text: 'We may use information for internal business purposes, including account administration, billing, customer relationship management, troubleshooting, analytics, service improvement, security monitoring, fraud prevention, and maintaining appropriate business records.',
    },
    {
      n: '08',
      title: 'Marketing',
      text: 'Where permitted by applicable law, we may use business contact information to communicate information about SHIVKMS products, services, updates, offers, or other business-related communications. You may request that we stop sending promotional communications at any time.',
    },
  ],

  sharing: [
    {
      n: '09',
      title: 'Service Providers',
      text: 'We may share information with trusted service providers who perform services on our behalf, such as hosting, cloud infrastructure, payment processing, email delivery, analytics, technical support, security, communications, or other business functions. Such providers are expected to use information only as necessary to provide their services.',
    },
    {
      n: '10',
      title: 'Client-Controlled Data',
      text: 'For SHIVKMS business software and customized applications, information entered into the system by a client or its authorized users may be controlled by that client. SHIVKMS may process such information solely to provide, maintain, secure, troubleshoot, or support the applicable service, subject to the applicable client agreement.',
    },
    {
      n: '11',
      title: 'Legal Requirements',
      text: 'We may disclose information when reasonably necessary to comply with applicable law, regulation, legal process, court order, governmental request, or to protect the rights, property, security, or safety of SHIVKMS, our clients, users, or others.',
    },
    {
      n: '12',
      title: 'Business Transfers',
      text: 'If SHIVKMS is involved in a merger, acquisition, restructuring, financing, sale of assets, or similar business transaction, information may be transferred as part of that transaction, subject to applicable legal requirements.',
    },
  ],

  cookies: [
    {
      n: '13',
      title: 'Cookies',
      text: 'Our website may use cookies and similar technologies to remember preferences, maintain functionality, understand website usage, improve performance, and support security. Some cookies may be placed by third-party service providers used by SHIVKMS.',
    },
    {
      n: '14',
      title: 'Analytics',
      text: 'We may use analytics technologies to understand how visitors use our website, including pages viewed, general traffic patterns, and website performance. Analytics information is used to improve the website and our services.',
    },
    {
      n: '15',
      title: 'Cookie Controls',
      text: 'Most web browsers allow users to control or disable cookies through browser settings. Disabling certain cookies may affect the functionality or availability of portions of our website.',
    },
  ],

  security: [
    {
      n: '16',
      title: 'Security Measures',
      text: 'SHIVKMS uses reasonable administrative, technical, and organizational measures designed to protect information against unauthorized access, alteration, disclosure, misuse, or destruction. Security measures may include access controls, authentication mechanisms, secure communications, monitoring, and other safeguards appropriate to the nature of the information.',
    },
    {
      n: '17',
      title: 'Client Responsibilities',
      text: 'Clients are responsible for maintaining the confidentiality of their account credentials, configuring appropriate user permissions, protecting devices and networks used to access SHIVKMS services, and maintaining appropriate backups where applicable.',
    },
    {
      n: '18',
      title: 'No Absolute Security Guarantee',
      text: 'No internet transmission, storage system, or electronic security measure can be guaranteed to be completely secure. While we take reasonable precautions, SHIVKMS cannot guarantee absolute security of information transmitted to or stored through our services.',
    },
  ],

  retention: [
    {
      n: '19',
      title: 'Retention Period',
      text: 'We retain information for as long as reasonably necessary to provide the applicable services, maintain business and financial records, comply with legal obligations, resolve disputes, enforce agreements, maintain security, and protect our legitimate business interests.',
    },
    {
      n: '20',
      title: 'Deletion Requests',
      text: 'Where applicable, users may request deletion of personal information by contacting SHIVKMS. Certain information may need to be retained where required by law, necessary for legitimate business purposes, required to complete a transaction, or necessary to establish, exercise, or defend legal claims.',
    },
  ],

  rights: [
    {
      n: '21',
      title: 'General Privacy Requests',
      text: 'Depending on where you reside and applicable law, you may have rights concerning personal information we hold about you. These may include requesting access to, correction of, deletion of, or information about the processing of your personal information.',
    },
    {
      n: '22',
      title: 'U.S. State Privacy Laws',
      text: 'Certain U.S. states provide residents with specific privacy rights. Depending on the applicable state law and whether SHIVKMS is subject to that law, eligible residents may have additional rights relating to access, correction, deletion, portability, opting out of certain processing activities, or other privacy choices.',
    },
    {
      n: '23',
      title: 'How to Submit a Request',
      text: 'To submit a privacy-related request, contact us using the email address provided in the Contact Us section. We may need to verify your identity before fulfilling certain requests in order to protect personal information from unauthorized disclosure.',
    },
    {
      n: '24',
      title: 'Non-Discrimination',
      text: 'Where required by applicable law, SHIVKMS will not discriminate against an individual for exercising privacy rights available to them under applicable law.',
    },
  ],

  children: [
    {
      n: '25',
      title: "Children's Information",
      text: 'Our website and general business services are not directed toward children under 13 years of age. We do not knowingly collect personal information directly from children under 13 without appropriate authorization. If you believe a child has provided personal information to us improperly, please contact us so that we can review and take appropriate action.',
    },
  ],

  changes: [
    {
      n: '26',
      title: 'Updates to This Policy',
      text: 'SHIVKMS may update this Privacy Policy from time to time to reflect changes in our services, technology, business practices, or applicable legal requirements. When material changes are made, we may provide notice through our website, client portal, email, or other appropriate communication channels.',
    },
    {
      n: '27',
      title: 'Effective Date',
      text: 'The updated version of this Privacy Policy becomes effective on the date stated at the beginning of the updated policy unless otherwise specified.',
    },
  ],
};

const REF_NO = 'SHIVKMS/PP/2026/01';
const LAST_UPDATED = '1st January, 2026';

function SectionLink({ id, label, active, onClick }) {
  return (
    <button
      className={`terms-nav-link ${active ? 'active' : ''}`}
      onClick={() => onClick(id)}
    >
      {label}
    </button>
  );
}

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('introduction');

  const scrollTo = (id) => {
    setActiveSection(id);

    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleScroll = (e) => {
    const container = e.currentTarget;

    const sections = SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);

    let current = sections[0]?.id;

    for (const el of sections) {
      if (el.getBoundingClientRect().top - 160 <= 0) {
        current = el.id;
      }
    }

    setActiveSection(current);
  };

  return (
    <div className="terms-page">
      <SEO
        title="Privacy Policy"
        description="Read the SHIVKMS Privacy Policy to understand how we collect, use, protect, retain, and disclose personal information through our website, software, applications, and services."
        keywords="SHIVKMS privacy policy, SHIVKMS data privacy, software privacy policy, US privacy policy"
        path="/privacy-policy"
      />

      {/* Hero */}
      <section className="terms-hero">
        <div className="terms-hero-bg" />

        <div className="container">
          <div className="terms-hero-inner">
            <span className="eyebrow">Privacy & Data Protection</span>

            <h1>
              Privacy <span className="gradient-text">Policy</span>
            </h1>

            <p>
              How SHIVKMS collects, uses, protects, and manages personal
              information through our website, software, applications, and
              services.
            </p>

            <div className="terms-meta">
              <span>
                Ref No: <strong>{REF_NO}</strong>
              </span>

              <span className="terms-meta-sep">·</span>

              <span>
                Last updated: <strong>{LAST_UPDATED}</strong>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="terms-body section-py">
        <div className="container">
          <div className="terms-layout">

            {/* Sidebar */}
            <aside className="terms-sidebar">
              <p className="terms-sidebar-title">Contents</p>

              <nav className="terms-nav">
                {SECTIONS.map((s) => (
                  <SectionLink
                    key={s.id}
                    id={s.id}
                    label={s.label}
                    active={activeSection === s.id}
                    onClick={scrollTo}
                  />
                ))}
              </nav>

              <div className="terms-support-card">
                <p className="terms-support-title">
                  Privacy Questions?
                </p>

                <p className="terms-support-sub">
                  Contact our team if you have questions about this Privacy
                  Policy or your personal information.
                </p>

                <a
                  href="tel:+919212552928"
                  className="terms-support-link"
                >
                  <Phone size={14} /> +91 9212552928
                </a>

                <a
                  href="mailto:contact@shivkms.com"
                  className="terms-support-link"
                >
                  <Mail size={14} /> contact@shivkms.com
                </a>

                <Link
                  to="/contact"
                  className="btn-primary terms-support-btn"
                >
                  Contact Support →
                </Link>
              </div>
            </aside>

            {/* Main content */}
            <div
              className="terms-content"
              onScroll={handleScroll}
            >

              {/* Introduction */}
              <div id="introduction" className="terms-section">
                <h2 className="terms-section-title">
                  Introduction
                </h2>

                <p className="terms-intro-text">
                  SHIVKMS ("SHIVKMS", "we", "us", or "our") respects
                  your privacy and is committed to handling personal
                  information responsibly. This Privacy Policy explains
                  how we collect, use, disclose, retain, and protect
                  information when you visit our website, communicate with
                  us, purchase our services, or use SHIVKMS software,
                  applications, portals, and related services.
                </p>

                <p className="terms-intro-text">
                  By accessing or using our website or services, you
                  acknowledge that you have read this Privacy Policy.
                  Where consent is required by applicable law, we will
                  obtain the appropriate consent before processing
                  information for that purpose.
                </p>

                <div className="terms-highlight-box">
                  <p>
                    <strong>Important:</strong> SHIVKMS may provide
                    customized business software and applications for
                    organizations. Information entered into those systems
                    by a client may be controlled by that client. In such
                    circumstances, SHIVKMS may process that information
                    on the client's behalf to provide hosting, support,
                    maintenance, security, or other contracted services.
                  </p>
                </div>
              </div>

              {/* Information */}
              <div id="information" className="terms-section">
                <h2 className="terms-section-title">
                  Information We Collect
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.information.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Use */}
              <div id="use" className="terms-section">
                <h2 className="terms-section-title">
                  How We Use Information
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.use.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sharing */}
              <div id="sharing" className="terms-section">
                <h2 className="terms-section-title">
                  Information Sharing
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.sharing.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cookies */}
              <div id="cookies" className="terms-section">
                <h2 className="terms-section-title">
                  Cookies & Tracking Technologies
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.cookies.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Security */}
              <div id="security" className="terms-section">
                <h2 className="terms-section-title">
                  Data Security
                </h2>

                <div className="terms-highlight-box">
                  <p>
                    <strong>Security Commitment:</strong> We use
                    reasonable safeguards designed to protect personal
                    information against unauthorized access, use,
                    alteration, disclosure, or destruction.
                  </p>
                </div>

                <div className="terms-clauses">
                  {CLAUSES.security.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Retention */}
              <div id="retention" className="terms-section">
                <h2 className="terms-section-title">
                  Data Retention & Deletion
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.retention.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rights */}
              <div id="rights" className="terms-section">
                <h2 className="terms-section-title">
                  Your Privacy Rights
                </h2>

                <div className="terms-highlight-box terms-highlight-warning">
                  <p>
                    <strong>U.S. Residents:</strong> Depending on your
                    state of residence and applicable law, you may have
                    specific rights regarding your personal information.
                    These rights may differ based on the applicable
                    state privacy law and the nature of our relationship
                    with you.
                  </p>
                </div>

                <div className="terms-clauses">
                  {CLAUSES.rights.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Children */}
              <div id="children" className="terms-section">
                <h2 className="terms-section-title">
                  Children's Privacy
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.children.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Changes */}
              <div id="changes" className="terms-section">
                <h2 className="terms-section-title">
                  Changes to This Privacy Policy
                </h2>

                <div className="terms-clauses">
                  {CLAUSES.changes.map((c) => (
                    <div key={c.n} className="terms-clause">
                      <span className="clause-n">{c.n}</span>

                      <div>
                        <h3 className="clause-title">
                          {c.title}
                        </h3>

                        <p className="clause-text">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div id="contact" className="terms-section">
                <h2 className="terms-section-title">
                  Contact Us
                </h2>

                <p className="terms-intro-text">
                  If you have questions about this Privacy Policy,
                  wish to exercise an applicable privacy right, or have
                  concerns about how your personal information is
                  handled, please contact SHIVKMS using the information
                  below.
                </p>

                <div className="terms-highlight-box">
                  <p>
                    <strong>SHIVKMS</strong>
                    <br />
                    Email:{' '}
                    <a href="mailto:contact@shivkms.com">
                      contact@shivkms.com
                    </a>
                    <br />
                    Phone:{' '}
                    <a href="tel:+919212552928">
                      +91 9212552928
                    </a>
                  </p>
                </div>

                <div className="terms-final-note">
                  <p>
                    <strong>Important:</strong> This Privacy Policy
                    describes SHIVKMS's general privacy practices. Where
                    a separate written agreement, data processing
                    agreement, enterprise agreement, or other contract
                    applies to a particular client service, the terms of
                    that agreement may govern the processing of client
                    data to the extent permitted by applicable law.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
}